import { For, Show, createComputed, createMemo, createSignal, on } from "solid-js";
import { api } from "../api/client";
import { t } from "../i18n";
import type { ExerciseType } from "../lib/activity";
import { createCachedResource } from "../lib/cache";
import { layoutSeries, nearestIndex, toViewX } from "../lib/chart";
import { progressKey } from "../lib/data";
import {
  DEFAULT_PROGRESS_RANGE,
  PROGRESS_RANGES,
  RANGE_LABEL,
  defaultProgressKind,
  formatProgressRange,
  formatProgressValue,
  type ProgressRange,
  type ProgressSeries,
} from "../lib/progress";
import { formatRecordDate, recordKindLabel } from "../lib/records";

/** Draw a dot per point up to this many; longer series show only the selected one. */
const DOTS_MAX = 40;
const CHART_HEIGHT = 140;

/**
 * Progress of one catalog exercise over workouts (best est. 1RM / duration / distance).
 * One cached request per range; the previous range stays on screen while the next loads.
 * Nothing is rendered until the first answer, or when the backend has no progress endpoint.
 */
export default function ExerciseProgress(props: { exerciseId: string; exerciseType?: ExerciseType | null }) {
  const [range, setRange] = createSignal<ProgressRange>(DEFAULT_PROGRESS_RANGE);
  const [series] = createCachedResource(
    () => (props.exerciseId ? { id: props.exerciseId, range: range() } : false),
    {
      key: (s: { id: string; range: ProgressRange }) => progressKey(s.id, s.range),
      fetch: (s) => api.exerciseProgress(s.id, s.range, defaultProgressKind(props.exerciseType)),
    },
  );

  // Last series that arrived for this exercise (any range): shown while another range loads.
  const [last, setLast] = createSignal<{ id: string; v: ProgressSeries } | null>(null);
  createComputed(() => {
    const v = series();
    if (v) setLast({ id: props.exerciseId, v });
  });
  const previous = () => (last()?.id === props.exerciseId ? last()!.v : null);

  const view = createMemo<ProgressSeries | null>(() => {
    const cur = series();
    if (cur) return cur;
    if (cur === undefined && series.error) {
      const prev = previous();
      return prev ? { kind: prev.kind, points: [] } : null;
    }
    return cur === undefined ? previous() : null;
  });
  const stale = () => series() === undefined && !series.error;

  const layout = createMemo(() => {
    const v = view();
    return v && v.points.length >= 2
      ? layoutSeries(v.points, (p) => p.value, { h: CHART_HEIGHT })
      : null;
  });

  // null = the latest point.
  const [picked, setPicked] = createSignal<number | null>(null);
  createComputed(on(() => view()?.points, () => setPicked(null), { defer: true }));
  const selected = () => {
    const L = layout();
    if (!L) return -1;
    const i = picked();
    return i != null && i < L.coords.length ? i : L.coords.length - 1;
  };

  function pick(e: PointerEvent & { currentTarget: SVGSVGElement }) {
    const L = layout();
    if (!L) return;
    const x = toViewX(e.clientX, e.currentTarget.getBoundingClientRect(), L.w);
    setPicked(nearestIndex(L.coords, x));
  }

  return (
    <Show when={view()}>
      {(v) => (
        <section class="tg-section-wrap" data-testid="exercise-progress">
          <h2 class="tg-section-header">
            {t("PROGRESS")} · {recordKindLabel(v().kind)}
          </h2>
          <div class="tg-segmented mb-2" role="tablist" aria-label={t("PROGRESS")}>
            <For each={PROGRESS_RANGES}>
              {(r) => (
                <button
                  type="button"
                  role="tab"
                  data-range={r}
                  aria-selected={range() === r}
                  onClick={() => setRange(r)}
                >
                  {t(RANGE_LABEL[r])}
                </button>
              )}
            </For>
          </div>
          <div class="tg-section">
            <div class="px-4 py-3" classList={{ "opacity-60": stale() }} aria-busy={stale()}>
              <Show
                when={layout()}
                fallback={
                  <div
                    class="flex items-center justify-center text-[15px] text-ink-muted"
                    style={{ height: `${CHART_HEIGHT}px` }}
                    data-testid="progress-empty"
                  >
                    {t("PROGRESS_NEED_TWO")}
                  </div>
                }
              >
                {(L) => {
                  const sel = () => L().coords[Math.min(selected(), L().coords.length - 1)];
                  return (
                    <div class="space-y-2">
                      <div class="flex items-baseline justify-between gap-3 text-[15px]" aria-live="polite">
                        <span class="text-ink-muted" data-testid="progress-date">
                          {formatRecordDate(sel().date) || sel().date}
                        </span>
                        <span class="font-medium tabular-nums" data-testid="progress-value">
                          {formatProgressValue(v().kind, sel().value)}
                        </span>
                      </div>
                      <svg
                        viewBox={`0 0 ${L().w} ${L().h}`}
                        class="h-auto w-full touch-pan-y select-none overflow-visible"
                        role="img"
                        aria-label={t("PROGRESS_CHART")}
                        data-testid="progress-chart"
                        onPointerDown={pick}
                        onPointerMove={(e) => {
                          if (e.pointerType === "mouse" || e.buttons) pick(e);
                        }}
                      >
                        <defs>
                          <linearGradient id="progressFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stop-color="var(--accent)" stop-opacity="0.28" />
                            <stop offset="100%" stop-color="var(--accent)" stop-opacity="0" />
                          </linearGradient>
                        </defs>
                        <line
                          x1={L().padX}
                          y1={L().h / 2}
                          x2={L().w - L().padX}
                          y2={L().h / 2}
                          stroke="var(--separator)"
                          stroke-width="1"
                        />
                        <path d={L().area} fill="url(#progressFill)" />
                        <path
                          d={L().line}
                          fill="none"
                          stroke="var(--accent)"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <line
                          x1={sel().x}
                          y1={L().padY}
                          x2={sel().x}
                          y2={L().h - L().padY}
                          stroke="var(--accent)"
                          stroke-opacity="0.4"
                          stroke-width="1"
                        />
                        <Show when={L().coords.length <= DOTS_MAX}>
                          <For each={L().coords}>
                            {(c) => (
                              <circle
                                cx={c.x}
                                cy={c.y}
                                r="3.5"
                                fill="var(--surface)"
                                stroke="var(--accent)"
                                stroke-width="2"
                                class="pointer-events-none"
                                data-testid="progress-dot"
                              />
                            )}
                          </For>
                        </Show>
                        <circle
                          cx={sel().x}
                          cy={sel().y}
                          r="5"
                          fill="var(--accent)"
                          class="pointer-events-none"
                          data-testid="progress-selected"
                        />
                      </svg>
                      <div class="flex justify-between text-[13px] text-ink-muted">
                        <span>{formatRecordDate(L().coords[0].date) || L().coords[0].date}</span>
                        <span>{formatProgressRange(v().kind, L().lo, L().hi)}</span>
                        <span>
                          {formatRecordDate(L().coords[L().coords.length - 1].date) ||
                            L().coords[L().coords.length - 1].date}
                        </span>
                      </div>
                    </div>
                  );
                }}
              </Show>
            </div>
          </div>
        </section>
      )}
    </Show>
  );
}
